import { Box, Stack, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import dayjs from 'dayjs';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { MdAccessAlarm, MdCheck, MdOutlinePhone, MdOutlinePhoneCallback } from 'react-icons/md';
import DateRangePicker from 'components/Common/DateRangePicker';
import * as urls from 'routes/config';
import { useGetPerformance } from '@/apis/business/agent/performance/hook';
import { TaskCompletionCardItem } from '@/apis/business/agent/performance/types';
import TransferToStaffIcon from '@/assets/icons/TransferToStaffIcon';
import { formatDuration } from '@/utils/helperFunctions';
import AIRecommendationCentre from '../components/AIRecommendationCentre';
import PerformanceLoader from '../components/PerformanceLoader';
import { formatTaskCompletionLabel, resolveTaskCompletionConfig } from '../components/TaskCompleted';
import CallJourneyFunnel from './CallJourneyFunnel';
import { mapOutboundPerformance } from './types';

type SignalsDeepLinkParams = {
  task_completed?: string;
  workflow?: string;
  start_date?: string;
  end_date?: string;
};

const buildSignalsSearchParams = (params: SignalsDeepLinkParams) => {
  const search = new URLSearchParams();
  if (params.task_completed) search.set('task_completed', params.task_completed);
  if (params.workflow) search.set('workflow', params.workflow);
  if (params.start_date) search.set('start_date', params.start_date);
  if (params.end_date) search.set('end_date', params.end_date);
  return search.toString();
};

function formatCount(n: number) {
  return n.toLocaleString('en-US');
}

export default function OutboundPerformance() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { locationId, agentId } = useParams<{ locationId: string; agentId: string }>();
  const { control, watch } = useForm();

  const filters = watch();
  const dateArr = filters?.date?.split(',');
  const updatedFilters = {
    start_date: dateArr?.[0],
    end_date: dateArr?.[1]
  };

  const { data, isLoading } = useGetPerformance({ ...updatedFilters });
  const performanceData = mapOutboundPerformance(data);
  const overview = performanceData.overview;

  const metricCards: { title: string; value: number; icon: React.ReactNode; subValue?: React.ReactNode }[] = [
    {
      title: 'Calls Dialed',
      value: overview.calls_dialed,
      icon: <MdOutlinePhoneCallback size={16} />,
      subValue: (
        <Stack direction='row' alignItems='center' gap={0.5} color='text.secondary'>
          <Typography component='span' fontSize={12} color='text.secondary' lineHeight={1.2}>
            ·
          </Typography>
          <Typography component='span' fontSize={12} color='text.secondary' lineHeight={1.2}>
            {formatCount(overview.dnc_blocked)} Blocked by DNC
          </Typography>
        </Stack>
      )
    },
    {
      title: 'Calls Answered',
      value: overview.calls_answered,
      icon: <MdOutlinePhone size={16} />,
      subValue: (
        <Stack direction='row' alignItems='center' gap={0.5} color='text.secondary' flexWrap='wrap'>
          <Typography component='span' fontSize={12} color='text.secondary' lineHeight={1.2}>
            ·
          </Typography>
          <Typography component='span' fontSize={12} color='text.secondary' lineHeight={1.2}>
            {formatCount(overview.answered_human)} Human
          </Typography>
          <Typography component='span' fontSize={12} color='text.secondary' lineHeight={1.2}>
            ·
          </Typography>
          <Typography component='span' fontSize={12} color='text.secondary' lineHeight={1.2}>
            {formatCount(overview.answered_voicemail)} Voicemail & IVR
          </Typography>
        </Stack>
      )
    },
    {
      title: 'Calls Successful',
      value: overview.calls_successful,
      icon: <MdCheck size={16} />
    },
    {
      title: 'Transferred to Staff',
      value: overview.transferred_to_staff,
      icon: (
        <Box display='flex' sx={{ '& svg': { width: 16, height: 16 } }}>
          <TransferToStaffIcon />
        </Box>
      )
    }
  ];

  const tasksCompletion = performanceData.tasks_completion;
  const taskCompletionCard: TaskCompletionCardItem[] = (() => {
    const rows = Array.isArray(tasksCompletion) ? tasksCompletion : [];
    const nameCounts = rows.reduce<Record<string, number>>((acc, row) => {
      const key = row.name || '';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    return rows.map((row) => {
      const showType = (nameCounts[row.name] || 0) > 1;
      const config = resolveTaskCompletionConfig(row.name, row.type, row.executor_label, row.worksheet_name);

      return {
        workflowKey: row.name,
        label: formatTaskCompletionLabel(row.name, row.type, showType, row.worksheet_name, row.executor_label),
        value: row.value ?? 0,
        type: row.type,
        icon: config.icon
      };
    });
  })();

  const navigateToSignals = (linkParams: Pick<SignalsDeepLinkParams, 'task_completed' | 'workflow'>) => {
    if (!locationId || !agentId) return;

    const query = buildSignalsSearchParams({
      ...linkParams,
      start_date: updatedFilters.start_date,
      end_date: updatedFilters.end_date
    });

    navigate(`${urls.agentBase(locationId, agentId)}/${urls.home.signals}${query ? `?${query}` : ''}`);
  };

  const handleTaskCardClick = (workflowKey: string) => {
    navigateToSignals({ task_completed: workflowKey });
  };

  if (isLoading) {
    return <PerformanceLoader />;
  }

  return (
    <>
      <AIRecommendationCentre />

      <Box
        display='flex'
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', lg: 'center' }}
        mb={3}
        flexDirection={{ xs: 'column', lg: 'row' }}
        gap={2}
      >
        <Box>
          <Typography variant='h6' fontWeight={500} sx={{ fontSize: { xs: '1.125rem', lg: '1.25rem' } }}>
            Performance
          </Typography>
          <Typography fontSize={14} color='text.secondary'>
            Track call volume, outcomes, & agent effectiveness over time.
          </Typography>
        </Box>

        <Box width={{ xs: '100%', lg: 260 }}>
          <DateRangePicker
            name='date'
            control={control}
            defaultValue={`${dayjs().subtract(30, 'day').format('YYYY-MM-DD')},${dayjs().format('YYYY-MM-DD')}`}
          />
        </Box>
      </Box>

      <Box
        bgcolor='#fff'
        border='1px solid #ECECEC'
        borderLeft={`4px solid ${theme.palette.primary.main}`}
        borderRadius={2}
        padding='16px'
        mb={3}
      >
        <Grid container spacing={2} alignItems='center'>
          <Grid size={{ xs: 12, lg: 6 }} display='flex' direction='row' gap={2.5}>
            <Box padding='20px' bgcolor={theme.palette.primary.light} borderRadius='12px'>
              <MdAccessAlarm size={26} color={theme.palette.primary.main} />
            </Box>
            <Box>
              <Typography fontSize={14} color='text.secondary' fontWeight={400}>
                Total Staff Time Saved
              </Typography>
              <Typography fontSize={24} fontWeight={500} color={theme.palette.primary.main}>
                {formatDuration(overview.total_call_duration)}
              </Typography>
              <Typography color='text.secondary' fontSize={12}>
                Equivalent to{' '}
                <Box component='span' color='primary.main'>
                  ~{overview.working_days} working
                </Box>{' '}
                days this period
              </Typography>
            </Box>
          </Grid>

          <Grid size={{ xs: 6, lg: 3 }} borderLeft='1px solid #ECECEC' paddingLeft={3}>
            <Typography fontSize={14} color='text.secondary'>
              Avg Duration
            </Typography>
            <Typography fontWeight={500} fontSize={20} color={theme.palette.primary.main}>
              {formatDuration(overview.average_duration)}
            </Typography>
          </Grid>

          <Grid size={{ xs: 6, lg: 3 }} borderLeft='1px solid #ECECEC' paddingLeft={3}>
            <Typography fontSize={14} color='text.secondary'>
              Avg. Answered Rate
            </Typography>
            <Typography fontWeight={500} fontSize={20} color={theme.palette.primary.main}>
              {overview.avg_answered_rate}%
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={2} mb={3}>
        {metricCards.map((card) => (
          <Grid key={card.title} size={{ xs: 6, lg: 3 }}>
            <Box
              bgcolor='#fff'
              border='1px solid #ECECEC'
              borderRadius='12px'
              p='16px'
              borderLeft={`4px solid ${theme.palette.primary.main}`}
              height='100%'
              boxSizing='border-box'
              sx={{ boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)' }}
            >
              <Stack direction='row' justifyContent='space-between' alignItems='flex-start' mb={1.5}>
                <Typography fontWeight={500} color='text.secondary' fontSize={14} lineHeight={1.3}>
                  {card.title}
                </Typography>
                <Box
                  width={32}
                  height={32}
                  minWidth={32}
                  bgcolor={theme.palette.primary.light}
                  color={theme.palette.primary.main}
                  borderRadius='50%'
                  display='flex'
                  alignItems='center'
                  justifyContent='center'
                >
                  {card.icon}
                </Box>
              </Stack>
              <Stack direction='row' alignItems='center' gap={0.75} flexWrap='wrap'>
                <Typography fontSize={20} fontWeight={600} color='#09090A' lineHeight={1.2}>
                  {formatCount(card.value)}
                </Typography>
                {card.subValue}
              </Stack>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Box mb={3}>
        <CallJourneyFunnel stages={performanceData.call_journey} />
      </Box>

      <Box bgcolor='#fff' border='1px solid #ECECEC' borderRadius={2} p={3} mb={3}>
        <Typography fontSize={20} fontWeight={500}>
          Tasks Completed
        </Typography>
        <Typography fontSize={14} color='text.secondary' mb={3}>
          Actions autonomously completed by your AI agent.
        </Typography>

        <Grid container spacing={2}>
          {taskCompletionCard.map((item: TaskCompletionCardItem) => (
            <Grid key={`${item.workflowKey}-${item.type || 'default'}`} size={{ xs: 6, sm: 4, md: 2 }}>
              <Box
                component='button'
                type='button'
                onClick={() => handleTaskCardClick(item.workflowKey)}
                border='1px solid #ECECEC'
                borderRadius='12px'
                p={2}
                textAlign='center'
                height='100%'
                width='100%'
                bgcolor='#fff'
                sx={{
                  cursor: 'pointer',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                  }
                }}
              >
                <Box
                  width={36}
                  height={36}
                  mx='auto'
                  mb={1}
                  borderRadius='8px'
                  display='flex'
                  alignItems='center'
                  justifyContent='center'
                >
                  {item.icon}
                </Box>
                <Typography fontSize={20} fontWeight={600} color='#09090A'>
                  {item.value}
                </Typography>
                <Typography fontSize={12} color='text.secondary' lineHeight='1.1rem' marginTop='0.2rem'>
                  {item.label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box bgcolor='#fff' border='1px solid #ECECEC' borderRadius={2} p={3}>
        <Typography fontSize={20} fontWeight={500}>
          Pickup Rate vs Hours Of Day
        </Typography>
        <Typography mb={2} fontSize={14} color='text.secondary'>
          Identify the best times to call by tracking pickup rates across different hours of the day.
        </Typography>

        <ResponsiveContainer width='100%' height={320}>
          <BarChart data={performanceData.pickup_rate_by_hour} margin={{ left: -10, top: 10, right: 8 }}>
            <CartesianGrid strokeDasharray='3 3' vertical={false} stroke='#F3F4F6' />
            <XAxis
              dataKey='time'
              tick={{ fontSize: 11, fill: '#6B7280' }}
              interval={0}
              angle={-20}
              textAnchor='end'
              height={50}
            />
            <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} />
            <Tooltip />
            <Bar dataKey='value' barSize={40} fill={theme.palette.primary.main} radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </>
  );
}
